<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .tickets-viewer-page
    //- Header
    .page-header
      button(@click="router.back()" type="button").back-button
        img(src="@/assets/icons/chevron-right.svg" alt="Back").back-icon
        span My Tickets

    //- Processing notice — shown when arriving from checkout while polling
    .processing-notice(v-if="isPolling && !tickets.length")
      .spinner
      p.processing-text Your tickets are being issued, please wait...

    //- Loading (non-polling initial fetch)
    .loading-state(v-else-if="loading && !tickets.length")
      .spinner
      p.loading-text Loading your tickets...

    //- Tickets grid
    .tickets-grid(v-else-if="tickets.length")
      .ticket-card(v-for="ticket in tickets" :key="ticket.id")
        .ticket-qr
          qrcode-vue(:value="ticketQrUrl(ticket)" :size="200" level="H")
        .ticket-info
          .ticket-tier {{ getTicketTierName(ticket) }}
          .ticket-option(v-if="ticket.selectedOption") {{ ticket.selectedOption }}
          .ticket-seat(v-if="ticket.section || ticket.row || ticket.seat")
            span(v-if="ticket.section") Section {{ ticket.section }}
            span(v-if="ticket.row") &nbsp;· Row {{ ticket.row }}
            span(v-if="ticket.seat") &nbsp;· Seat {{ ticket.seat }}
          .ticket-status
            .checked-in(v-if="ticket.checkedInAt")
              img(src="@/assets/icons/check.svg" alt="").status-icon
              span Checked in {{ formatDateTime(ticket.checkedInAt) }}
            .not-checked-in(v-else)
              span Not yet checked in
        .ticket-code-text {{ ticket.code }}

    //- Empty state
    .empty-state(v-else-if="!loading && !isPolling")
      .empty-icon 🎟
      h3.empty-title No tickets found
      p.empty-subtitle No tickets were found for this event.
      el-button(type="primary" @click="fetchTickets" round).refresh-btn Refresh
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import dayjs from 'dayjs';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import { findMyTickets, findTicketAvailability } from '@/graphs/gql';
import {
  buildVariantTitleMap,
  resolveTicketTierName,
} from '@/utils/ticketDisplay';
import { ticketLog } from '@/utils/ticketLogger';

interface IssuedTicket {
  id: string;
  code: string;
  tierName: string;
  selectedOption?: string;
  stickerId: string;
  variantId: string;
  section?: string;
  row?: string;
  seat?: string;
  checkedInAt?: string;
  createdAt: string;
}

const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default defineComponent({
  name: 'TicketsViewer',
  components: { WebAppLayout, QrcodeVue },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const me = computed(() => store.getters['users/me']);

    const stickerId = String(route.params.stickerId);
    const loading = ref(false);
    const isPolling = ref(false);
    const tickets = ref<IssuedTicket[]>([]);
    const variantTitleMap = ref<Record<string, string>>({});

    async function loadVariantTitles() {
      try {
        const token = me.value?.token ?? null;
        const response = await makeRequest(
          'query',
          findTicketAvailability,
          { stickerId },
          token ? { token } : null,
        );
        const tiers = response?.data?.findTicketAvailability || [];
        variantTitleMap.value = buildVariantTitleMap(tiers);
      } catch (err) {
        console.error('Failed to load ticket tiers:', err);
      }
    }

    function getTicketTierName(ticket: IssuedTicket) {
      return resolveTicketTierName(ticket, variantTitleMap.value);
    }

    async function fetchTickets() {
      loading.value = true;
      try {
        const token = me.value?.token ?? null;
        const response = await makeRequest(
          'query',
          findMyTickets,
          { stickerId },
          token ? { token } : null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        tickets.value = response?.data?.findMyTickets || [];
        ticketLog('TicketsViewer loaded tickets', {
          stickerId,
          count: tickets.value.length,
          tickets: tickets.value.map((ticket) => ({
            id: ticket.id,
            variantId: ticket.variantId,
            tierName: ticket.tierName,
            resolvedName: getTicketTierName(ticket),
          })),
        });
      } catch (err) {
        console.error('Failed to load tickets:', err);
      } finally {
        loading.value = false;
      }
    }

    async function pollForTickets() {
      isPolling.value = true;
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await fetchTickets();
        if (tickets.value.length) break;
        if (i < MAX_POLL_ATTEMPTS - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => { setTimeout(resolve, POLL_INTERVAL_MS); });
        }
      }
      isPolling.value = false;
    }

    onMounted(async () => {
      await loadVariantTitles();
      const fromCheckout = route.query.fromCheckout === 'true';
      if (fromCheckout) {
        await pollForTickets();
      } else {
        await fetchTickets();
      }
    });

    function formatDateTime(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    function ticketQrUrl(ticket: IssuedTicket) {
      return `${window.location.origin}/web/ticket/${ticket.stickerId}/detail?ticketId=${ticket.id}`;
    }

    return {
      tickets,
      loading,
      isPolling,
      router,
      fetchTickets,
      formatDateTime,
      ticketQrUrl,
      getTicketTierName,
    };
  },
});
</script>

<style lang="scss" scoped>
.tickets-viewer-page {
  min-height: 100vh;
  background: #f5f5f7;
  padding-bottom: 4rem;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.back-icon {
  width: 1.125rem;
  height: 1.125rem;
  transform: rotate(180deg);
  opacity: 0.6;
}

// Processing / loading states
.processing-notice,
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  gap: 1rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e0e0e0;
  border-top-color: #5865f2;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.processing-text,
.loading-text {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

// Tickets grid
.tickets-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.ticket-card {
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  .ticket-qr {
    background: #f9fafb;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 32px;
    border-bottom: 2px dashed #e5e7eb;
  }

  .ticket-info {
    padding: 20px;
  }

  .ticket-tier {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 4px;
  }

  .ticket-option {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 8px;
  }

  .ticket-seat {
    font-size: 13px;
    color: #555;
    margin-bottom: 12px;
  }

  .ticket-status {
    margin-top: 4px;
  }

  .checked-in {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #0d9488;
    font-size: 13px;
    font-weight: 600;

    .status-icon {
      width: 16px;
      height: 16px;
    }
  }

  .not-checked-in {
    font-size: 13px;
    color: #9ca3af;
  }

  .ticket-code-text {
    padding: 12px 20px;
    background: #f9fafb;
    font-size: 11px;
    font-family: monospace;
    color: #9ca3af;
    text-align: center;
    border-top: 1px solid #e5e7eb;
    word-break: break-all;
  }
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 1.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem;
}

.empty-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 1.5rem;
}

.refresh-btn {
  min-width: 140px;
  font-weight: 600;
}
</style>
